import { useState, useEffect, useCallback, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, Loader2, Search } from "lucide-react";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { SearchBar } from "@/components/search/SearchBar";
import { TeacherCard } from "@/components/teacher/discovery/TeacherCard";
import { FilterPanel, DEFAULT_FILTERS, type FiltersState } from "@/components/search/FilterPanel";
import { ActiveFilterChips } from "@/components/search/ActiveFilterChips";
import { MoreFiltersDrawer } from "@/components/search/MoreFiltersDrawer";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useTeacherSearch } from "@/hooks/use-marketplace-search";
import { useGeoDefaults } from "@/hooks/useGeoDefaults";
import { usePageTitle } from "@/hooks/usePageTitle";
import { useAuth } from "@/hooks/use-auth-compat";
import { markSearchVisited } from "@/hooks/useNextSteps";
import type { TeacherSearchResult } from "@/services/marketplaceSearchService";
import { BlankSearchState } from "./components/BlankSearchState";
import { EmptyState } from "@/components/common/EmptyState";
import type { SearchContextSnapshot } from "@/types/searchResults";
import { ZeroResultsState } from "./components/ZeroResultsState";
import { TeacherProfileSheet } from "@/components/teacher/discovery/TeacherProfileSheet";

function TeacherCardSkeleton() {
  return (
    <div className="rounded-xl border border-border/60 overflow-hidden">
      <Skeleton className="aspect-[3/2] w-full" />
      <div className="p-4 space-y-3">
        <Skeleton className="h-5 w-3/4" />
        <Skeleton className="h-4 w-1/2" />
        <div className="flex gap-1.5">
          <Skeleton className="h-5 w-16 rounded-full" />
          <Skeleton className="h-5 w-16 rounded-full" />
        </div>
        <Skeleton className="h-9 w-full rounded-lg" />
      </div>
    </div>
  );
}

/** Count how many filter fields differ from defaults (for badge on mobile trigger) */
function countActiveFilters(f: FiltersState): number {
  let count = 0;
  if (f.sortBy && f.sortBy !== 'RELEVANCE') count++;
  if (f.offeringType && f.offeringType !== 'ALL') count++;
  if (f.gradeBand.length > 0) count++;
  if (f.language.length > 0) count++;
  if (f.curriculum.length > 0) count++;
  if (f.availabilityTimeOfDay) count++;
  if (f.trialSessionAvailable === true) count++;
  if (f.targetExamId) count++;
  if (f.topicKeywords) count++;
  if (f.priceMin != null || f.priceMax != null) count++;
  return count;
}

/** Parse URL search params into FiltersState */
function filtersFromURL(sp: URLSearchParams): FiltersState {
  return {
    sortBy: sp.get('sortBy') || 'RELEVANCE',
    priceMin: sp.get('priceMin') ? Number(sp.get('priceMin')) : null,
    priceMax: sp.get('priceMax') ? Number(sp.get('priceMax')) : null,
    offeringType: sp.get('offeringType') || 'ALL',
    gradeBand: sp.getAll('gradeBand'),
    language: sp.getAll('language'),
    curriculum: sp.getAll('curriculum'),
    availabilityTimeOfDay: sp.get('availabilityTimeOfDay') || '',
    trialSessionAvailable: sp.get('trialSessionAvailable') === 'true' ? true : null,
    targetExamId: sp.get('targetExamId') || '',
    topicKeywords: sp.get('topicKeywords') || '',
  };
}

/** Write FiltersState back to URLSearchParams, preserving non-filter params */
function applyFiltersToURL(prev: URLSearchParams, filters: FiltersState): URLSearchParams {
  const next = new URLSearchParams(prev);

  // Reset page on any filter change
  next.set('page', '0');

  // sortBy
  if (filters.sortBy && filters.sortBy !== 'RELEVANCE') {
    next.set('sortBy', filters.sortBy);
  } else {
    next.delete('sortBy');
  }

  // price
  if (filters.priceMin != null) next.set('priceMin', String(filters.priceMin));
  else next.delete('priceMin');
  if (filters.priceMax != null) next.set('priceMax', String(filters.priceMax));
  else next.delete('priceMax');

  // offeringType
  if (filters.offeringType && filters.offeringType !== 'ALL') {
    next.set('offeringType', filters.offeringType);
  } else {
    next.delete('offeringType');
  }

  // gradeBand (multi-value)
  next.delete('gradeBand');
  filters.gradeBand.forEach(v => next.append('gradeBand', v));

  // language (multi-value)
  next.delete('language');
  filters.language.forEach(v => next.append('language', v));

  // curriculum (multi-value)
  next.delete('curriculum');
  filters.curriculum.forEach(v => next.append('curriculum', v));

  // availabilityTimeOfDay
  if (filters.availabilityTimeOfDay) next.set('availabilityTimeOfDay', filters.availabilityTimeOfDay);
  else next.delete('availabilityTimeOfDay');

  // trialSessionAvailable
  if (filters.trialSessionAvailable === true) next.set('trialSessionAvailable', 'true');
  else next.delete('trialSessionAvailable');

  // targetExamId
  if (filters.targetExamId) next.set('targetExamId', filters.targetExamId);
  else next.delete('targetExamId');

  // topicKeywords
  if (filters.topicKeywords) next.set('topicKeywords', filters.topicKeywords);
  else next.delete('topicKeywords');

  return next;
}

/** Returns true when the URL has any filter params set (used to decide goal-profile pre-population) */
function hasFilterParams(sp: URLSearchParams): boolean {
  const filterKeys = [
    'sortBy', 'priceMin', 'priceMax', 'offeringType',
    'gradeBand', 'language', 'curriculum', 'availabilityTimeOfDay',
    'trialSessionAvailable', 'targetExamId', 'topicKeywords',
  ];
  return filterKeys.some(k => sp.has(k));
}

export default function SearchResultsPage({ embedded = false, onBack }: { embedded?: boolean; onBack?: () => void }) {
  usePageTitle('Search Results | BookMyCoaching');
  const [searchParams, setSearchParams] = useSearchParams();

  // Completes the "Browse teachers" step in the student next-steps checklist.
  useEffect(() => {
    markSearchVisited();
  }, []);
  const navigate = useNavigate();
  const [previewHandle, setPreviewHandle] = useState<string | null>(null);

  const q = searchParams.get("q") || undefined;
  const pageParam = Number(searchParams.get("page") || "0");
  const filters = filtersFromURL(searchParams);

  // No search intent when URL has no keyword and no user-chosen filter params.
  const hasSearched = !!q || hasFilterParams(searchParams);

  const { user } = useAuth();
  const accessToken = user?.access_token;

  const { data: geoDefaults } = useGeoDefaults(accessToken);

  const apiOfferingType = filters.offeringType === 'ALL' ? undefined : filters.offeringType;

  // Declared before useMemo so `data` is not in the temporal dead zone when
  // useMemo evaluates its dependency array — Vite/Rollup minifies `data` to a
  // single letter and a TDZ reference crashes the production bundle.
  const { data, isLoading, isError, refetch } = useTeacherSearch({
    q,
    page: pageParam,
    size: 20,
    subjectId: searchParams.get('subjectId') || undefined,
    latitude: searchParams.get('latitude') ? Number(searchParams.get('latitude')) : undefined,
    longitude: searchParams.get('longitude') ? Number(searchParams.get('longitude')) : undefined,
    radiusKm: searchParams.get('radiusKm') ? Number(searchParams.get('radiusKm')) : undefined,
    offeringType: apiOfferingType,
    gradeBand: filters.gradeBand.length > 0 ? filters.gradeBand : undefined,
    language: filters.language.length > 0 ? filters.language : undefined,
    curriculum: filters.curriculum.length > 0 ? filters.curriculum : undefined,
    availabilityTimeOfDay: filters.availabilityTimeOfDay || undefined,
    trialSessionAvailable: filters.trialSessionAvailable ?? undefined,
    targetExamId: filters.targetExamId || undefined,
    topicKeywords: filters.topicKeywords || undefined,
    priceMin: filters.priceMin ?? undefined,
    priceMax: filters.priceMax ?? undefined,
    sortBy: filters.sortBy !== 'RELEVANCE' ? filters.sortBy : undefined,
    accessToken: accessToken || undefined,
  }, hasSearched);

  // useMemo for referential stability: same object identity when contents haven't changed.
  // Required by TeacherCard's useEffect dep on searchContext — prevents spurious re-fetches.
  // subject comes from parsedIntent.subject (resolved catalog name) NOT raw q: if the query
  // didn't resolve to a catalog subject, subject is undefined so the breakdown falls back to
  // goal profile — matching what the tile does.
  const searchContext = useMemo<SearchContextSnapshot | null>(() => ({
    subject: data?.parsedIntent?.subject ?? undefined,
    gradeBand: filters.gradeBand.length > 0 ? filters.gradeBand[0] : undefined,
    curriculum: filters.curriculum.length > 0 ? filters.curriculum[0] : undefined,
    budgetMax: filters.priceMax ?? undefined,
    examId: filters.targetExamId || undefined,
    language: filters.language.length > 0 ? filters.language[0] : undefined,
  }), [
    data?.parsedIntent?.subject,
    filters.priceMax,
    filters.targetExamId,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    filters.gradeBand.join(','),
    filters.curriculum.join(','),
    filters.language.join(','),
  ]);

  // Write search context to sessionStorage so TeacherProfilePage can read it
  // when the user clicks a CTA and the registration gate modal fires.
  useEffect(() => {
    const ctx = {
      searchQuery: q ?? null,
      activeFilters: {
        sortBy: filters.sortBy,
        offeringType: filters.offeringType,
        gradeBands: filters.gradeBand,
        language: filters.language,
        curriculum: filters.curriculum,
      },
    };
    sessionStorage.setItem('bmc:search-context', JSON.stringify(ctx));
  }, [q, filters.sortBy, filters.offeringType, filters.gradeBand, filters.language, filters.curriculum]);

  const handleFiltersChange = useCallback(
    (patch: Partial<FiltersState>) => {
      setSearchParams(prev => {
        const current = filtersFromURL(prev);
        const merged: FiltersState = { ...current, ...patch };
        return applyFiltersToURL(prev, merged);
      });
    },
    [setSearchParams]
  );

  const handleRadiusChange = useCallback(
    (km: number) => {
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        next.set('radiusKm', String(km));
        next.set('page', '0');
        return next;
      });
    },
    [setSearchParams]
  );

  const handlePageChange = (newPage: number) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set("page", String(newPage));
      return next;
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleCardClick = (teacher: TeacherSearchResult) => {
    setPreviewHandle(teacher.handle ?? teacher.teacherProfileId);
  };

  const hasLocation = !!searchParams.get('latitude');
  const radiusKm = searchParams.get('radiusKm') ? Number(searchParams.get('radiusKm')) : 25;

  const totalElements = data?.totalElements ?? 0;
  const totalPages = data?.totalPages ?? 0;
  const teachers = data?.content ?? [];
  const fallbackMode = data?.fallbackMode ?? false;
  const closestFits = data?.closestFits ?? [];
  const resolvedSubjectId = data?.resolvedSubjectId ?? undefined;
  const activeCount = countActiveFilters(filters);
  const priceMax = geoDefaults?.maxPrice ?? 5000;
  // A bare free-text/subject search (no filter chips) still has meaningful context to
  // notify on once the backend has identified a subject for it — not just filter chips.
  const hasSearchContext = activeCount > 0 || !!searchParams.get('subjectId') || !!q || !!resolvedSubjectId;

  return (
    <div className={embedded ? "flex flex-col min-h-full" : "min-h-screen bg-background"}>
      {!embedded && <Header />}

      {/* ── Embedded dashboard header ── */}
      {embedded && (
        <div className="relative z-30 px-4 sm:px-6 pt-4 sm:pt-6 pb-4 shrink-0">
          <div className="rounded-2xl border border-[#DCDDEE] bg-white shadow-sm">
            <div className="flex flex-col sm:flex-row">
              {/* Left: title + search */}
              <div className="relative flex-1 px-5 sm:px-6 py-4 sm:py-5 bg-sky-50 rounded-2xl">
                {/* Decorative blobs — clipped separately so the search dropdown isn't cut off */}
                <div className="absolute inset-0 rounded-2xl overflow-hidden pointer-events-none">
                  <div className="absolute -top-6 -left-6 w-32 h-32 bg-sky-400/10 rounded-full blur-2xl" />
                  <div className="absolute bottom-0 right-0 w-24 h-24 bg-indigo-400/10 rounded-full blur-2xl" />
                </div>
                <div className="relative z-10">
                  <div className="flex items-center gap-1.5 mb-2">
                    <Search className="w-3.5 h-3.5 text-sky-600" />
                    <span className="text-xs font-bold uppercase tracking-widest text-sky-600">Find Teachers</span>
                  </div>
                  <h1 className="text-xl sm:text-2xl font-bold text-[#262A7A] mb-3">
                    Find Your Perfect Teacher
                  </h1>
                  <div className="max-w-3xl">
                    <SearchBar
                      initialQuery={q}
                      onSearch={(query, _topicId, loc) => {
                        const next = new URLSearchParams();
                        if (query) next.set("q", query);
                        if (loc) {
                          next.set("latitude", String(loc.lat));
                          next.set("longitude", String(loc.lng));
                          next.set("radiusKm", "25");
                        }
                        next.set("page", "0");
                        setSearchParams(next);
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Public hero (standalone page only) ── */}
      {!embedded && (
        <div className="pt-28">
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-b from-[#d0eef8]/60 via-[#b8e0ef]/50 to-[#8fcfdf]/60 pointer-events-none" />
            <div className="absolute inset-0 pointer-events-none overflow-hidden">
              <div className="absolute -top-24 -right-24 w-[500px] h-[500px] bg-[hsl(197,70%,75%)]/30 rounded-full blur-[120px]" />
              <div className="absolute -bottom-32 -left-24 w-[400px] h-[400px] bg-[hsl(260,60%,75%)]/20 rounded-full blur-[100px]" />
            </div>
            <div className="absolute inset-0 pointer-events-none overflow-hidden opacity-[0.06]">
              <svg className="w-full h-full">
                <defs>
                  <pattern id="search-hero-grid" x="0" y="0" width="40" height="40" patternUnits="userSpaceOnUse">
                    <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#1A4A55" strokeWidth="0.5" />
                  </pattern>
                </defs>
                <rect width="100%" height="100%" fill="url(#search-hero-grid)" />
              </svg>
            </div>
            <div className="container mx-auto px-4 relative z-10 py-10 pb-7">
              <div className="text-center mb-6">
                <h1 className="font-sans text-3xl md:text-4xl text-[#262A7A] mb-2 leading-tight">
                  Find Your <span className="text-[hsl(210,60%,18%)]">Perfect Teacher</span>
                </h1>
                <p className="text-[#5A5E80] text-sm">Verified experts across 50+ subjects</p>
              </div>
              <div className="max-w-3xl mx-auto">
                <SearchBar
                  initialQuery={q}
                  onSearch={(query, _topicId, loc) => {
                    const next = new URLSearchParams();
                    if (query) next.set("q", query);
                    if (loc) {
                      next.set("latitude", String(loc.lat));
                      next.set("longitude", String(loc.lng));
                      next.set("radiusKm", "25");
                    }
                    next.set("page", "0");
                    setSearchParams(next);
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      <div className={embedded ? "flex-1 px-4 sm:px-6 py-4 overflow-auto" : "container mx-auto px-4 py-8"}>
        {/* Mobile: filter trigger row */}
        <div className="flex lg:hidden items-center justify-between gap-4 mb-4">
          <MoreFiltersDrawer
            filters={filters}
            onFiltersChange={handleFiltersChange}
            priceMax={priceMax}
            activeCount={activeCount}
            currency={geoDefaults?.currency}
            hasLocation={hasLocation}
            radiusKm={radiusKm}
            onRadiusChange={handleRadiusChange}
          />
          {hasSearched && (
            <div className="text-sm text-muted-foreground">
              {isLoading ? (
                <span className="flex items-center gap-1.5">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  Searching…
                </span>
              ) : (
                <span>
                  <span className="font-bold text-foreground">{totalElements}</span> teachers
                </span>
              )}
            </div>
          )}
        </div>

        {/* Active filter chips (both mobile and desktop) */}
        {activeCount > 0 && (
          <div className="mb-4">
            <ActiveFilterChips
              filters={filters}
              onFiltersChange={handleFiltersChange}
              currency={geoDefaults?.currency}
            />
          </div>
        )}

        {/* Two-column layout: desktop sidebar + results */}
        <div className="flex gap-8">
          {/* Desktop sidebar */}
          <aside className="hidden lg:block w-[280px] shrink-0">
            <div className={`sticky ${embedded ? "top-4" : "top-24"} max-h-[calc(100vh-7rem)] overflow-y-auto pr-2 pb-8`}>
              <FilterPanel
                filters={filters}
                onFiltersChange={handleFiltersChange}
                priceMax={priceMax}
                currency={geoDefaults?.currency}
                hasLocation={hasLocation}
                radiusKm={radiusKm}
                onRadiusChange={handleRadiusChange}
              />
            </div>
          </aside>

          {/* Results column */}
          <div className="flex-1 min-w-0">
            {!hasSearched ? (
              <BlankSearchState />
            ) : (
            <>
            {/* Results count — desktop */}
            <div className="hidden lg:flex items-center justify-between mb-5">
              <div className="text-sm text-muted-foreground">
                {isLoading ? (
                  <span className="flex items-center gap-1.5">
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    Searching…
                  </span>
                ) : (
                  <>
                    <span className="font-bold text-foreground">{totalElements}</span>
                    {" "}teachers found
                    {q && (
                      <> for <span className="font-medium text-foreground">"{q}"</span></>
                    )}
                  </>
                )}
              </div>
            </div>

            {isLoading && (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
                {Array.from({ length: 6 }).map((_, i) => (
                  <TeacherCardSkeleton key={i} />
                ))}
              </div>
            )}

            {isError && (
              <EmptyState
                icon={Search}
                title="Something went wrong"
                description="Please try again."
                iconBgClassName="bg-red-50"
                iconClassName="text-red-400"
                action={<Button onClick={() => refetch()} variant="outline" className="rounded-full px-6 cursor-pointer">Retry</Button>}
              />
            )}

            {!isLoading && !isError && teachers.length === 0 && !fallbackMode && (
              <ZeroResultsState
                filters={{
                  subjectId: resolvedSubjectId ?? searchParams.get('subjectId') ?? undefined,
                  gradeBand: filters.gradeBand.length > 0 ? filters.gradeBand[0] : undefined,
                  curriculum: filters.curriculum.length > 0 ? filters.curriculum[0] : undefined,
                  offeringType: filters.offeringType !== 'ALL' ? filters.offeringType : undefined,
                  priceMin: filters.priceMin ?? undefined,
                  priceMax: filters.priceMax ?? undefined,
                  currency: geoDefaults?.currency ?? undefined,
                }}
                hasActiveFilters={hasSearchContext}
                onClearFilters={() => handleFiltersChange(DEFAULT_FILTERS)}
                accessToken={accessToken}
              />
            )}

            {!isLoading && !isError && fallbackMode && closestFits.length === 0 && (
              <ZeroResultsState
                filters={{
                  subjectId: resolvedSubjectId ?? searchParams.get('subjectId') ?? undefined,
                  gradeBand: filters.gradeBand.length > 0 ? filters.gradeBand[0] : undefined,
                  curriculum: filters.curriculum.length > 0 ? filters.curriculum[0] : undefined,
                  offeringType: filters.offeringType !== 'ALL' ? filters.offeringType : undefined,
                  priceMin: filters.priceMin ?? undefined,
                  priceMax: filters.priceMax ?? undefined,
                  currency: geoDefaults?.currency ?? undefined,
                }}
                hasActiveFilters={hasSearchContext}
                onClearFilters={() => handleFiltersChange(DEFAULT_FILTERS)}
                accessToken={accessToken}
              />
            )}

            {!isLoading && !isError && fallbackMode && closestFits.length > 0 && (
              <>
                <div className="mb-5 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                  No exact matches found for your filters. Showing closest available teachers:
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
                  {closestFits.map((teacher) => (
                    <TeacherCard
                      key={teacher.teacherProfileId}
                      teacher={teacher}
                      onClick={() => handleCardClick(teacher)}
                      searchContext={searchContext}
                    />
                  ))}
                </div>
              </>
            )}

            {!isLoading && !isError && teachers.length > 0 && (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
                  {teachers.map((teacher) => (
                    <TeacherCard
                      key={teacher.teacherProfileId}
                      teacher={teacher}
                      onClick={() => handleCardClick(teacher)}
                      searchContext={searchContext}
                    />
                  ))}
                </div>

                {totalPages > 1 && (
                  <div className="flex items-center justify-center gap-3 mt-10">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(pageParam - 1)}
                      disabled={pageParam === 0}
                      className="cursor-pointer"
                    >
                      <ChevronLeft className="w-4 h-4 mr-1" />
                      Previous
                    </Button>
                    <span className="text-sm text-muted-foreground px-2">
                      Page {pageParam + 1} of {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(pageParam + 1)}
                      disabled={pageParam >= totalPages - 1}
                      className="cursor-pointer"
                    >
                      Next
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                )}
              </>
            )}
            </>
            )}
          </div>
        </div>
      </div>

      {!embedded && <Footer />}

      <TeacherProfileSheet
        handle={previewHandle}
        onClose={() => setPreviewHandle(null)}
      />
    </div>
  );
}
